import { CANVAS_TUNING } from "./tuning";

/** Shared mobile breakpoint -- must match the CSS @media threshold in style.css */
export const MOBILE_BREAKPOINT_PX = 768;

/** Minimum font size on mobile to keep text readable on small screens. */
const MOBILE_MIN_FONT_PX = 9;

/** Minimum font size on desktop to keep small text legible at scale. */
const DESKTOP_MIN_FONT_PX = 7.5;

/**
 * Enforces a minimum font floor per viewport:
 * - Mobile: min 9px
 * - Desktop: min 7px
 */
export function mobileFontSize(px: number): number {
  return isMobileViewport() ? Math.max(px, MOBILE_MIN_FONT_PX) : Math.max(px, DESKTOP_MIN_FONT_PX);
}

/**
 * Returns true when the current viewport qualifies as a mobile device.
 *
 * Primary signal: viewport width <= MOBILE_BREAKPOINT_PX.
 * Secondary (advisory only): coarse pointer detection.
 * SSR/test safe: returns false when `window` is unavailable.
 */
export function isMobileViewport(): boolean {
  if (typeof window === "undefined") return false;
  return window.innerWidth <= MOBILE_BREAKPOINT_PX;
}

/**
 * Returns true when the viewport is a mobile landscape orientation.
 * Only applies landscape logic on mobile devices -- desktop is always
 * treated as non-landscape regardless of aspect ratio.
 * SSR/test safe: returns false when `window` is unavailable.
 */
export function isLandscapeViewport(): boolean {
  if (typeof window === "undefined") return false;
  return isMobileViewport() && window.innerWidth > window.innerHeight;
}

/**
 * Computes the logical canvas dimensions for the current viewport.
 *
 * Desktop: returns the static CANVAS_TUNING defaults (360x600).
 * Mobile portrait: keeps width at 360 and derives height from the screen
 * aspect ratio, clamped to [480, 900].
 * Mobile landscape: height-first scaling. Width stays 360, height derived
 * from aspect ratio and clamped to [480, 900]. The CSS handles rendering
 * this canvas at full viewport height (see landscape media query).
 */
/**
 * Cached reduced-motion media query result.
 * Lazily initialised on first call and updated via the `change` event.
 */
let _reducedMotionMql: MediaQueryList | null = null;
let _reducedMotionValue = false;

/**
 * Returns `true` when the user's OS / browser has "reduce motion" enabled.
 * SSR / test safe: returns `false` when `window` is unavailable.
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  if (_reducedMotionMql == null) {
    _reducedMotionMql = window.matchMedia("(prefers-reduced-motion: reduce)");
    _reducedMotionValue = _reducedMotionMql.matches;
    _reducedMotionMql.addEventListener("change", (e) => {
      _reducedMotionValue = e.matches;
    });
  }
  return _reducedMotionValue;
}

export function computeCanvasDimensions(): { width: number; height: number } {
  if (!isMobileViewport()) {
    return { width: CANVAS_TUNING.width, height: CANVAS_TUNING.height };
  }

  const screenWidth = window.innerWidth;
  // Use the same height source as the CSS --app-height variable (syncViewportHeightVar).
  // In PWA standalone mode, innerHeight includes area behind the notch/home indicator
  // while visualViewport.height may not — causing non-uniform canvas stretching.
  const screenHeight = window.visualViewport?.height ?? window.innerHeight;
  const width = CANVAS_TUNING.width; // always 360
  const rawHeight = Math.round(width * (screenHeight / screenWidth));
  const height = Math.min(900, Math.max(480, rawHeight));

  return { width, height };
}
